// Admin module configuration: columns, form fields, filters per resource.
export const STAGES = ["Registration", "Profile Completion", "Documents", "Document Verification",
  "Screening", "Interview", "Selection", "Offer", "Signed Offer", "Medical", "MOL / Contract",
  "Visa / Processing", "Joining / Ticket", "Deployment"];

const t = (name, label, type = "text", opts) => ({ name, label, type, opts });
const MARITAL = ["Single", "Married", "Divorced", "Widowed", "Prefer not to say"];
const EDUCATION = ["No formal education", "Primary", "Secondary (10th)", "Higher Secondary (12th)", "ITI / Trade Certificate", "Diploma", "Bachelor's Degree", "Master's Degree", "Doctorate"];
const EMPLOYMENT = ["Employed", "Unemployed", "Self-employed", "Working abroad", "Student"];

export const CONFIG = {
  candidates: {
    title: "Candidates",
    columns: [["code", "ID"], ["full_name", "Name"], ["position", "Position"], ["work_location", "Location"], ["current_stage", "Stage", "stage"], ["current_status", "Status", "status"]],
    fields: [t("full_name", "Full Name"), t("given_name", "Given Name"), t("surname", "Surname"),
      t("fusion_id", "Fusion ID"), t("passport_number", "Passport No."), t("nationality", "Nationality", "nationality"),
      { ...t("date_of_birth", "Date of Birth", "date"), max: "today" }, t("gender", "Gender", "select", ["Male", "Female", "Other", "Prefer not to say"]),
      t("marital_status", "Marital Status", "select", MARITAL), t("mobile", "Mobile", "phone"), t("email", "Email"),
      t("country", "Country of Residence", "country"), t("address", "Address"), t("position", "Position"),
      t("education", "Education Level", "select", EDUCATION), t("employment_status", "Employment Status", "select", EMPLOYMENT),
      t("work_location", "Work Location"), t("passport_expiry_date", "Passport Expiry", "date"),
      t("current_stage", "Stage", "select", STAGES), t("current_status", "Status")],
    filters: [{ name: "current_stage", label: "Stage", options: STAGES }],
  },
  agencies: {
    title: "Agencies",
    columns: [["code", "ID"], ["name", "Name"], ["contact_person", "Contact"], ["country", "Country"], ["commission_amount", "Commission"], ["status", "Status", "status"]],
    fields: [t("name", "Agency Name"), t("contact_person", "Contact Person"), t("email", "Email"),
      t("phone", "Phone", "phone"), t("country", "Country", "country"), t("address", "Address"),
      t("status", "Status", "select", ["Pending", "Approved", "Rejected", "Suspended"]),
      t("commission_type", "Commission Type"), t("commission_amount", "Commission Amount", "number")],
    filters: [{ name: "status", label: "Status", options: ["Pending", "Approved", "Rejected"] }],
  },
  clients: {
    title: "Employers / Clients",
    columns: [["code", "ID"], ["company_name", "Company"], ["country", "Country"], ["industry", "Industry"], ["status", "Status", "status"]],
    fields: [t("company_name", "Company Name"), t("contact_person", "Contact Person"), t("email", "Email"),
      t("phone", "Phone", "phone"), t("country", "Country", "country"), t("industry", "Industry"), t("address", "Address"),
      t("status", "Status", "select", ["Active", "Inactive"])],
  },
  jobs: {
    title: "Jobs",
    columns: [["code", "ID"], ["title", "Title"], ["country", "Country"], ["vacancies", "Vacancies"], ["salary", "Salary"], ["status", "Status", "status"]],
    fields: [t("title", "Job Title"), t("position", "Position"), t("client_name", "Client"),
      t("country", "Country", "country"), t("location", "Location"), t("vacancies", "Vacancies", "number"),
      t("salary", "Salary"), t("experience", "Experience"), t("skills", "Skills"),
      t("requirements", "Requirements", "textarea"),
      t("category", "Category", "select", ["Driving", "Construction", "Hospitality", "Manufacturing", "Security", "Healthcare", "Engineering", "General"]),
      t("description", "Job Description", "textarea"), t("responsibilities", "Responsibilities", "textarea"),
      t("qualifications", "Qualifications", "textarea"), t("contract_duration", "Contract Duration"),
      t("working_hours", "Working Hours"), t("gender", "Gender Requirement", "select", ["Any", "Male", "Female"]),
      t("age_limit", "Age Limit"), t("interview_location", "Interview Location"), t("interview_date", "Interview Date", "date"),
      ...[["accommodation", "Accommodation"], ["food", "Food"], ["transport", "Transport"], ["visa_provided", "Visa Provided"],
        ["air_ticket", "Air Ticket"], ["medical_insurance", "Medical Insurance"]].map(([k, l]) => t(k, l, "select", ["Yes", "No"])),
      t("status", "Status", "select", ["Draft", "Published", "Paused", "Closed"])],
    filters: [{ name: "status", label: "Status", options: ["Draft", "Published", "Paused", "Closed"] }],
  },
  projects: {
    title: "Projects",
    columns: [["code", "ID"], ["name", "Name"], ["client_name", "Client"], ["country", "Country"], ["vacancies", "Vacancies"], ["status", "Status", "status"]],
    fields: [t("name", "Project Name"), t("client_name", "Client"), t("country", "Country", "country"),
      t("location", "Location"), t("vacancies", "Vacancies", "number"), t("deadline", "Deadline", "date"),
      t("status", "Status", "select", ["Active", "On Hold", "Completed"])],
  },
  interviews: {
    title: "Interviews",
    columns: [["code", "ID"], ["candidate_name", "Candidate"], ["job_title", "Job"], ["date", "Date", "date"], ["status", "Status", "status"]],
    fields: [t("candidate_name", "Candidate"), t("job_title", "Job"), t("date", "Date", "datetime-local"),
      t("interviewer", "Interviewer"), t("location", "Location"), t("meeting_link", "Meeting Link"),
      t("status", "Status", "select", ["Scheduled", "Confirmed", "Completed", "Rescheduled", "Cancelled", "Selected", "Rejected", "Pending Result"]),
      t("result", "Result"), t("remarks", "Remarks", "textarea")],
    filters: [{ name: "status", label: "Status", options: ["Scheduled", "Completed", "Selected", "Rejected"] }],
  },
  offers: {
    title: "Offers",
    columns: [["code", "ID"], ["candidate_name", "Candidate"], ["position", "Position"], ["offer_date", "Date", "date"], ["status", "Status", "status"]],
    fields: [t("candidate_name", "Candidate"), t("client_name", "Client"), t("position", "Position"),
      t("offer_date", "Offer Date", "date"), t("status", "Status", "select", ["Pending", "Issued", "Accepted", "Rejected", "Expired"]), t("remarks", "Remarks", "textarea")],
  },
  medical: {
    title: "Medical",
    columns: [["code", "ID"], ["candidate_name", "Candidate"], ["date", "Date", "date"], ["center", "Center"], ["status", "Status", "status"]],
    fields: [t("candidate_name", "Candidate"), t("date", "Date", "date"), t("center", "Center"),
      t("status", "Status", "select", ["Pending", "Scheduled", "PASS/FIT", "FAIL/UNFIT", "Retest", "Review"]),
      t("result", "Result"), t("remarks", "Remarks", "textarea")],
  },
  contracts: {
    title: "MOL / Contracts",
    columns: [["code", "ID"], ["candidate_name", "Candidate"], ["status", "Status", "status"], ["created_at", "Created", "date"]],
    fields: [t("candidate_name", "Candidate"), t("status", "Status", "select", ["Pending", "Submitted", "Received", "Signed", "Rejected", "Completed"]), t("remarks", "Remarks", "textarea")],
  },
  visa: {
    title: "Visa / Processing",
    columns: [["code", "ID"], ["candidate_name", "Candidate"], ["status", "Status", "status"], ["created_at", "Created", "date"]],
    fields: [t("candidate_name", "Candidate"), t("status", "Status", "select", ["Initiated", "Documents Submitted", "Pending", "Approved", "Rejected", "Completed"]), t("remarks", "Remarks", "textarea")],
  },
  joining: {
    title: "Joining / Ticket",
    columns: [["code", "ID"], ["candidate_name", "Candidate"], ["status", "Status", "status"], ["created_at", "Created", "date"]],
    fields: [t("candidate_name", "Candidate"), t("travel_date", "Travel Date", "date"), t("flight_info", "Flight Info"),
      t("status", "Status", "select", ["Pending", "Ticket Requested", "Ticket Received", "Travel Scheduled", "Joined", "Cancelled"]), t("remarks", "Remarks", "textarea")],
  },
  deployments: {
    title: "Deployment",
    columns: [["code", "ID"], ["candidate_name", "Candidate"], ["client_name", "Client"], ["country", "Country"], ["status", "Status", "status"]],
    fields: [t("candidate_name", "Candidate"), t("client_name", "Client"), t("country", "Country", "country"),
      t("deployment_date", "Deployment Date", "date"), t("joining_date", "Joining Date", "date"),
      t("status", "Status", "select", ["Deployed", "Completed"]), t("remarks", "Remarks", "textarea")],
  },
  employees: {
    title: "Employees",
    columns: [["code", "ID"], ["name", "Name"], ["employer", "Employer"], ["country", "Country"], ["designation", "Designation"], ["employment_status", "Status", "status"]],
    fields: [t("name", "Name"), t("employer", "Employer"), t("country", "Country", "country"), t("department", "Department"),
      t("designation", "Designation"), t("joining_date", "Joining Date", "date"),
      t("employment_status", "Status", "select", ["Active", "On Leave", "Exited"])],
  },
  commissions: {
    title: "Commissions",
    columns: [["code", "ID"], ["agency_name", "Agency"], ["candidate_name", "Candidate"], ["amount", "Amount"], ["status", "Status", "status"]],
    fields: [t("agency_name", "Agency"), t("candidate_name", "Candidate"), t("client_name", "Client"),
      t("amount", "Amount", "number"), t("status", "Status", "select", ["Pending", "Paid"]),
      t("payment_date", "Payment Date", "date"), t("remarks", "Remarks", "textarea")],
    filters: [{ name: "status", label: "Status", options: ["Pending", "Paid"] }],
  },
  invoices: {
    title: "Invoices",
    columns: [["code", "ID"], ["client_name", "Client"], ["total", "Total"], ["paid_amount", "Paid"], ["status", "Status", "status"]],
    fields: [t("client_name", "Client"), t("country", "Country", "country"), t("amount", "Amount", "number"),
      t("total", "Total", "number"), t("paid_amount", "Paid Amount", "number"),
      t("status", "Status", "select", ["Draft", "Issued", "Paid", "Partially Paid", "Overdue", "Cancelled"])],
    filters: [{ name: "status", label: "Status", options: ["Issued", "Paid", "Partially Paid", "Overdue"] }],
  },
  payments: {
    title: "Payments",
    columns: [["code", "ID"], ["client_name", "Client"], ["amount", "Amount"], ["payment_method", "Method"], ["status", "Status", "status"]],
    fields: [t("client_name", "Client"), t("amount", "Amount", "number"), t("currency", "Currency"),
      t("payment_method", "Method", "select", ["Bank Transfer", "Cash", "UPI", "Card", "Cheque"]),
      t("transaction_id", "Transaction ID"), t("payment_date", "Date", "date"),
      t("status", "Status", "select", ["Pending", "Completed", "Failed"])],
  },
  expenses: {
    title: "Expenses",
    columns: [["code", "ID"], ["category", "Category"], ["client_name", "Client"], ["country", "Country"], ["amount", "Amount"]],
    fields: [t("category", "Category", "select", ["Visa Fees", "Medical", "Travel", "Processing", "Office", "Other"]),
      t("client_name", "Client"), t("country", "Country", "country"), t("amount", "Amount", "number"), t("date", "Date", "date")],
  },
  staff: {
    title: "Staff",
    columns: [["code", "ID"], ["name", "Name"], ["department", "Department"], ["designation", "Designation"], ["status", "Status", "status"]],
    fields: [t("name", "Name"), t("email", "Email"), t("phone", "Phone", "phone"),
      t("department", "Department", "select", ["Recruitment", "Processing", "HR", "Finance", "Operations"]),
      t("designation", "Designation"), t("joining_date", "Joining Date", "date"),
      t("status", "Status", "select", ["Active", "Inactive"])],
  },
  tasks: {
    title: "Tasks",
    columns: [["code", "ID"], ["title", "Title"], ["assigned_staff", "Assignee"], ["priority", "Priority"], ["due_date", "Due", "date"], ["status", "Status", "status"]],
    fields: [t("title", "Title"), t("description", "Description", "textarea"), t("assigned_staff", "Assigned Staff"),
      t("priority", "Priority", "select", ["Low", "Medium", "High"]), t("due_date", "Due Date", "date"),
      t("status", "Status", "select", ["Pending", "In Progress", "Completed", "Overdue", "Cancelled"])],
    filters: [{ name: "status", label: "Status", options: ["Pending", "In Progress", "Completed", "Overdue"] }],
  },
  attendance: {
    title: "Attendance",
    columns: [["code", "ID"], ["staff_name", "Staff"], ["date", "Date", "date"], ["status", "Status", "status"]],
    fields: [t("staff_name", "Staff"), t("date", "Date", "date"), t("status", "Status", "select", ["Present", "Absent", "Leave"]), t("remarks", "Remarks")],
  },
  leave: {
    title: "Leave",
    columns: [["code", "ID"], ["staff_name", "Staff"], ["leave_type", "Type"], ["start_date", "From", "date"], ["status", "Status", "status"]],
    fields: [t("staff_name", "Staff"), t("leave_type", "Type", "select", ["Casual", "Sick", "Earned", "Unpaid"]),
      t("start_date", "Start Date", "date"), t("end_date", "End Date", "date"), t("reason", "Reason", "textarea"),
      t("status", "Status", "select", ["Pending", "Approved", "Rejected"])],
  },
  enquiries: {
    title: "Enquiries",
    columns: [["code", "ID"], ["company_name", "Company"], ["country", "Country"], ["position", "Position"], ["workers", "Workers"], ["status", "Status", "status"]],
    fields: [t("company_name", "Company"), t("contact_person", "Contact"), t("email", "Email"), t("phone", "Phone", "phone"),
      t("country", "Country", "country"), t("position", "Position"), t("workers", "Workers", "number"),
      t("status", "Status", "select", ["New", "Contacted", "Converted", "Closed"]), t("message", "Message", "textarea")],
  },
  countries: {
    title: "Countries",
    columns: [["name", "Name"], ["code", "Code"], ["currency", "Currency"], ["medical_term", "Medical Term"], ["status", "Status", "status"]],
    fields: [t("name", "Name"), t("code", "Code"), t("currency", "Currency"), t("medical_term", "Medical Term"),
      t("status", "Status", "select", ["Active", "Inactive"])],
  },
  workflows: {
    title: "Workflow / SOP",
    columns: [["code", "ID"], ["name", "Name"], ["country", "Country"], ["is_default", "Default"]],
    fields: [t("name", "Name"), t("country", "Country", "country"), t("is_default", "Is Default", "select", ["true", "false"])],
  },
  users: {
    title: "Users",
    columns: [["name", "Name"], ["email", "Email"], ["role", "Role"], ["active", "Active"]],
    fields: [t("name", "Name"), t("email", "Email"), t("password", "Password (set/reset)", "password"),
      t("role", "Role", "select", ["super_admin", "admin", "recruitment_staff", "hr", "finance", "agency", "candidate"]),
      t("active", "Active", "select", ["true", "false"])],
  },
  notifications: {
    title: "Notifications",
    columns: [["title", "Title"], ["type", "Type"], ["audience", "Audience"], ["read", "Read"], ["created_at", "Created", "date"]],
    fields: [t("title", "Title"), t("message", "Message", "textarea"),
      t("type", "Type", "select", ["info", "warning", "alert"]), t("audience", "Audience", "select", ["admin", "agency", "candidate"])],
  },
  audit_logs: {
    title: "Audit Logs",
    columns: [["at", "Time", "date"], ["user", "User"], ["role", "Role"], ["action", "Action"], ["entity", "Entity"]],
    readOnly: true,
  },
};
